import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { getSlipById } from '../../api/commissionSlipApi';
import { getAllSlips } from '../../api/slipApi';
import { getExtraSlipsByAgent } from '../../api/extraSlipApi';
import { getIncentiveSlipsByAgentId } from '../../api/incentiveSlipApi';
import { useUser } from '../../context/userContext';
import SlipPdfFrame from '../accountant/slipPdfFrame';
import PdfBody from '../accountant/slipPdfBody';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { facetFilterOptions } from '../common/facetFilter';

const TYPE_FILTERS = ['All', 'Commission', 'Incentive', 'Other'];

const typeColors = {
  Commission: { bg: '#DBEAFE', color: '#1E3A8A' },
  Incentive: { bg: '#FCE7F3', color: '#9D174D' },
  Other: { bg: '#F3E8FF', color: '#6B21A8' },
};

const formatRM = (amount) => `RM ${new Intl.NumberFormat('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0)}`;
const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' }) : '\u2014';

const statusConfig = {
  Approved: { bg: '#D1FAE5', color: '#065F46' },
  Pending: { bg: '#FEF3C7', color: '#92400E' },
  Voided: { bg: '#FEE2E2', color: '#991B1B' },
  Rejected: { bg: '#F1F5F9', color: '#64748B' },
};

export default function MyCommissionSlips() {
  const { user } = useUser();
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewSlip, setViewSlip] = useState(null);
  const [viewOtherSlip, setViewOtherSlip] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const slipRenderRef = useRef(null);
  const [renderSlip, setRenderSlip] = useState(null);
  // Off-screen render target for non-Commission (Incentive / Other) PDF export.
  const richRenderRef = useRef(null);
  const [richRenderSlip, setRichRenderSlip] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Aggregate fan-out (Commission + Incentive + Other) scoped to current user.
      const data = await getAllSlips(typeFilter, 'mine');
      // Map SlipListItem → row shape compatible with existing commission UI.
      const rows = (Array.isArray(data) ? data : []).map(s => ({
        slipId: s.slipId,
        type: s.type,                 // "Commission" | "Incentive" | "Other"
        agentName: s.agentName,
        period: s.period,
        dateIssued: s.dateIssued,
        totalPayout: s.totalPayout,
        status: s.status,
        displayName: s.displayName,
        slipType: s.type === 'Commission' ? 'Agent' : s.type, // legacy column dispatch
      }));
      setSlips(rows);
    } catch (e) {
      console.error(e);
      setSlips([]);
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  const handleDownloadPdf = async (slip) => {
    try {
      const fullSlip = (await getSlipById(slip.slipId)).data;
      setRenderSlip(fullSlip);
      await new Promise(resolve => setTimeout(resolve, 300));

      const element = slipRenderRef.current;
      if (!element) { alert('PDF render failed'); return; }

      const canvas = await html2canvas(element, {
        scale: 2.5, useCORS: true, allowTaint: true, backgroundColor: '#ffffff', logging: false,
        width: element.scrollWidth, height: element.scrollHeight,
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdfWidth = (canvas.width * 25.4) / (96 * 2.5);
      const pdfHeight = (canvas.height * 25.4) / (96 * 2.5);

      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
        unit: 'mm', format: [pdfWidth, pdfHeight]
      });
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      pdf.save(`Commission_Slip_${slip.period?.replace(/\s+/g, '_') || 'slip'}.pdf`);
      setRenderSlip(null);
    } catch (e) {
      console.error('PDF generation failed:', e);
      alert('Failed to generate PDF: ' + e.message);
      setRenderSlip(null);
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleViewSlipRef = useRef();
  handleViewSlipRef.current = async (row) => {
    if (row.type === 'Commission' || row.type === 'Referral') {
      try {
        const res = await getSlipById(row.slipId);
        setViewSlip(res.data);
      } catch (e) { alert('Failed to load slip details'); }
      return;
    }
    // Incentive / Other: hydrate the full type-specific payload (reason,
    // description, slipData, etc.) via the agent-scoped endpoints so the
    // recipient sees the SAME rich preview the accountant does.
    try {
      const agentId = user?.userId;
      if (row.type === 'Other') {
        const list = await getExtraSlipsByAgent(agentId); // returns array
        const match = (list || []).find(s => s.extraSlipId === row.slipId);
        setViewOtherSlip(match ? { ...row, ...match, type: 'Other' } : { ...row, type: 'Other' });
      } else if (row.type === 'Incentive') {
        const res = await getIncentiveSlipsByAgentId(agentId); // axios response
        const list = res.data || [];
        const match = list.find(s => s.incentiveSlipId === row.slipId);
        setViewOtherSlip(match ? { ...row, ...match, type: 'Incentive' } : { ...row, type: 'Incentive' });
      } else {
        setViewOtherSlip({ ...row });
      }
    } catch (e) {
      console.error('Failed to load slip details:', e);
      // Fall back to the listing fields rather than blocking the view.
      setViewOtherSlip({ ...row });
    }
  };
  const handleDownloadPdfRef = useRef();
  handleDownloadPdfRef.current = handleDownloadPdf;

  // PDF export for the rich (Incentive / Other) preview — mirrors the
  // accountant's html2canvas → jsPDF pipeline using the shared SlipPdfFrame.
  const handleDownloadRichPdf = async (slip) => {
    try {
      setRichRenderSlip(slip);
      await new Promise(resolve => setTimeout(resolve, 300));
      const element = richRenderRef.current;
      if (!element) { alert('PDF render failed'); return; }
      const canvas = await html2canvas(element, {
        scale: 2.5, useCORS: true, allowTaint: true, backgroundColor: '#ffffff', logging: false,
        width: element.scrollWidth, height: element.scrollHeight,
      });
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdfWidth = (canvas.width * 25.4) / (96 * 2.5);
      const pdfHeight = (canvas.height * 25.4) / (96 * 2.5);
      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
        unit: 'mm', format: [pdfWidth, pdfHeight]
      });
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      pdf.save(`${slip.type}_Slip_${(slip.period || slip.displayName || 'slip').replace(/\s+/g, '_')}.pdf`);
      setRichRenderSlip(null);
    } catch (e) {
      console.error('PDF generation failed:', e);
      alert('Failed to generate PDF: ' + e.message);
      setRichRenderSlip(null);
    }
  };

  const columns = useMemo(() => [
    {
      id: 'no', header: 'No', size: 60, enableSorting: false, enableColumnFilter: false,
      Cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.index + 1}</span>,
    },
    {
      id: 'status', header: 'Status', accessorKey: 'status', size: 110,
      Cell: ({ cell }) => {
        const val = cell.getValue() || 'Pending';
        const cfg = statusConfig[val] || statusConfig.Pending;
        return <Chip label={val} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", backgroundColor: cfg.bg, color: cfg.color }} />;
      },
    },
    {
      id: 'type', header: 'Type', accessorKey: 'type', size: 110,
      Cell: ({ cell }) => {
        const val = cell.getValue() || '';
        const cfg = typeColors[val] || { bg: '#F1F5F9', color: '#64748B' };
        return <Chip label={val} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", backgroundColor: cfg.bg, color: cfg.color }} />;
      },
    },
    {
      id: 'name', header: 'Name', size: 220,
      accessorFn: row => {
        // A referral slip's period is already a plain month ("October 2026") rather than a
        // commission round's date span, so it needs no parsing — and calling it a
        // "Commission Slip" would be wrong in the one place the referrer reads it.
        if (row.type === 'Referral') return `RFC Slip (${row.period || ''})`;
        if (row.type === 'Commission') {
          try {
            const parts = (row.period || '').split('-');
            const endPart = (parts[1] || parts[0] || '').trim();
            const parsed = new Date(endPart);
            const monthYear = !isNaN(parsed.getTime()) ? parsed.toLocaleDateString('en-MY', { month: 'long', year: 'numeric' }) : row.period;
            return row.slipType === 'Astern' ? `Astern Comm Slip (${monthYear})` : `Commission Slip (${monthYear})`;
          } catch { return row.period || ''; }
        }
        return row.displayName || row.period || '';
      },
      Cell: ({ cell, row }) => (
        <span style={{ fontWeight: 600, color: row.original.slipType === 'Astern' ? '#6366F1' : '#0F172A' }}>
          {cell.getValue()}
        </span>
      ),
    },
    {
      id: 'totalPayout', header: 'Total Payout', accessorFn: row => row.totalPayout, size: 130,
      Cell: ({ row }) => <span style={{ fontWeight: 600 }}>{formatRM(row.original.totalPayout)}</span>,
    },
    { id: 'period', header: 'Period', accessorKey: 'period', size: 180 },
    {
      id: 'dateIssued', header: 'Date Issued', accessorFn: row => row.dateIssued, size: 120,
      Cell: ({ row }) => formatDate(row.original.dateIssued),
    },
    {
      id: 'actions', header: '', size: 140, enableSorting: false, enableColumnFilter: false,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" variant="outlined" startIcon={<VisibilityIcon sx={{ fontSize: '0.85rem !important' }} />}
            onClick={() => handleViewSlipRef.current(row.original)}
            sx={{ textTransform: 'none', fontSize: '0.72rem', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', minWidth: 0, px: 1.2 }}>View</Button>
          {(row.original.type === 'Commission' || row.original.type === 'Referral') && (
            <Button size="small" variant="outlined" startIcon={<DownloadIcon sx={{ fontSize: '0.85rem !important' }} />}
              onClick={() => handleDownloadPdfRef.current(row.original)}
              sx={{ textTransform: 'none', fontSize: '0.72rem', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', minWidth: 0, px: 1.2, color: '#64748B', borderColor: '#CBD5E1' }}>PDF</Button>
          )}
        </Box>
      ),
    },
  ], []);

  const filteredSlips = useMemo(() => statusFilter === 'All' ? slips : slips.filter(s => s.status === statusFilter), [slips, statusFilter]);

  const table = useMaterialReactTable({
        // Excel-style column filters: a search box over a tick-box list of the values
        // actually in that column, for every column at once. A column with nearly one
        // value per row falls back to the plain box on its own.
        // (user 2026-10-06: "for all table maybe yes. go ahead with this method")
        ...facetFilterOptions,
    columns, data: filteredSlips,
    enableRowSelection: false,
    getRowId: (row) => `${row.type}-${row.slipId}`,
    enableColumnActions: false, enableColumnDragging: false, enableDensityToggle: false,
    enableFullScreenToggle: false, enableHiding: false, enableStickyHeader: true, muiTableContainerProps: { sx: { maxHeight: 'unset' } },
    enableColumnFilters: false,
    initialState: { density: 'compact', pagination: { pageSize: 25 } },
    muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
    muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
    muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
    muiTableBodyRowProps: { sx: { '&:hover': { backgroundColor: '#F8FAFC' } } },
  });

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <Box sx={{ mb: 2 }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>My Slips</h2>
        <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>Your commission, incentive, and other slips</p>
      </Box>
      {/* Type filter pills */}
      <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {TYPE_FILTERS.map(t => (
          <button
            key={t}
            onClick={() => setTypeFilter(t)}
            style={{
              border: typeFilter === t ? '1px solid #1A8CFF' : '1px solid #E2E8F0',
              background: typeFilter === t ? 'linear-gradient(135deg, #1A8CFF, #0F62CC)' : 'white',
              color: typeFilter === t ? 'white' : '#475569',
              borderRadius: '8px',
              padding: '6px 16px',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: "'Poppins', sans-serif",
              transition: 'all 0.15s ease',
            }}
          >
            {t}
          </button>
        ))}
      </Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        {['All', 'Pending', 'Approved', 'Voided'].map(status => (
          <Button
            key={status}
            size="small"
            variant={statusFilter === status ? 'contained' : 'outlined'}
            onClick={() => setStatusFilter(status)}
            sx={{
              textTransform: 'none', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif",
              fontWeight: 600, borderRadius: '8px', px: 2, py: 0.5,
              ...(statusFilter === status ? {
                background: status === 'Pending' ? '#F59E0B' : status === 'Approved' ? '#10B981' : status === 'Voided' ? '#EF4444' : 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                color: 'white', border: 'none',
                '&:hover': { background: status === 'Pending' ? '#D97706' : status === 'Approved' ? '#059669' : status === 'Voided' ? '#DC2626' : '#0F62CC' },
              } : {
                borderColor: '#E2E8F0', color: '#64748B',
              }),
            }}
          >
            {status} {status !== 'All' ? `(${slips.filter(s => s.status === status).length})` : `(${slips.length})`}
          </Button>
        ))}
      </Box>
      <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <MaterialReactTable table={table} />
      </div>

      {/* View Slip Dialog */}
      {viewSlip && (
        <Dialog open={!!viewSlip} onClose={() => setViewSlip(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
          <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
            <span>Commission Slip</span>
            <IconButton onClick={() => setViewSlip(null)} size="small"><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              {(() => {
                const status = viewSlip.status || 'Pending';
                const cfg = statusConfig[status] || statusConfig.Pending;
                return <Chip label={status} size="small" sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", backgroundColor: cfg.bg, color: cfg.color }} />;
              })()}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
              <Box sx={{ background: '#F8FAFC', borderRadius: '8px', p: 1.5 }}>
                <span style={{ fontSize: '0.65rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontFamily: "'Poppins', sans-serif" }}>Period</span>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>{viewSlip.period}</div>
              </Box>
              <Box sx={{ background: '#F8FAFC', borderRadius: '8px', p: 1.5 }}>
                <span style={{ fontSize: '0.65rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontFamily: "'Poppins', sans-serif" }}>Total Payout</span>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1A8CFF', fontFamily: "'Poppins', sans-serif" }}>{formatRM(viewSlip.totalPayout)}</div>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
              <Box sx={{ background: '#F8FAFC', borderRadius: '8px', p: 1.5 }}>
                <span style={{ fontSize: '0.65rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontFamily: "'Poppins', sans-serif" }}>Date Issued</span>
                <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>{formatDate(viewSlip.dateIssued)}</div>
              </Box>
              <Box sx={{ background: '#F8FAFC', borderRadius: '8px', p: 1.5 }}>
                <span style={{ fontSize: '0.65rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontFamily: "'Poppins', sans-serif" }}>Status</span>
                <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>{viewSlip.status}</div>
              </Box>
            </Box>

            {viewSlip.voidedReason && (
              <Box sx={{ background: '#FEF2F2', borderRadius: '8px', p: 1.5, mb: 2 }}>
                <span style={{ fontSize: '0.65rem', color: '#991B1B', fontWeight: 600, textTransform: 'uppercase', fontFamily: "'Poppins', sans-serif" }}>Voided Reason</span>
                <div style={{ fontSize: '0.82rem', color: '#991B1B', fontFamily: "'Poppins', sans-serif" }}>{viewSlip.voidedReason}</div>
              </Box>
            )}

            {/* Commission Items */}
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8, fontFamily: "'Poppins', sans-serif" }}>Commission Items</div>
            {(() => {
              try {
                const items = JSON.parse(viewSlip.slipData);
                return (
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', fontFamily: "'Poppins', sans-serif" }}>
                      <thead>
                        <tr style={{ backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                          <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748B', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>Sale #</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748B', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>Customer</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', color: '#64748B', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>Type</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748B', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>Total Comm</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748B', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>1st Pay</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748B', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>2nd Pay</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748B', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>3rd Pay</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', color: '#64748B', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>Payout</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, i) => {
                          const repayTotal = (item.repayment || 0) + (item.repayment2 || 0) + (item.repayment3 || 0) + (item.repayment4 || 0) + (item.repayment5 || 0);
                          const latestRepayDate = [item.repayment5On, item.repayment4On, item.repayment3On, item.repayment2On, item.repaymentOn].find(d => d);
                          return (
                            <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                              <td style={{ padding: '6px 8px', color: '#1A8CFF', fontWeight: 500 }}>{item.salesId || '\u2014'}</td>
                              <td style={{ padding: '6px 8px', color: '#374151' }}>{item.customerName || '\u2014'}</td>
                              <td style={{ padding: '6px 8px', color: '#374151' }}>{item.type || '\u2014'}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#374151' }}>{formatRM(item.totalComm)}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#374151' }}>
                                {item.firstPayment != null ? formatRM(item.firstPayment) : '\u2014'}
                                {item.firstPaymentOn && <div style={{ fontSize: '0.6rem', color: '#94A3B8' }}>{formatDate(item.firstPaymentOn)}</div>}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#374151' }}>
                                {item.secondPayment != null ? formatRM(item.secondPayment) : '\u2014'}
                                {item.secondPaymentOn && <div style={{ fontSize: '0.6rem', color: '#94A3B8' }}>{formatDate(item.secondPaymentOn)}</div>}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: repayTotal < 0 ? '#DC2626' : '#374151' }}>
                                {repayTotal !== 0 ? formatRM(repayTotal) : '\u2014'}
                                {latestRepayDate && <div style={{ fontSize: '0.6rem', color: '#94A3B8' }}>{formatDate(latestRepayDate)}</div>}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#0F172A' }}>{formatRM(item.payout || item.totalComm || 0)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        {viewSlip.processingFee > 0 ? (
                          <>
                            <tr style={{ borderTop: '2px solid #E2E8F0' }}>
                              <td colSpan="7" style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Gross Comm</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>{formatRM(viewSlip.grossCommission)}</td>
                            </tr>
                            <tr>
                              <td colSpan="7" style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 500, color: '#64748B' }}>Processing Fee</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 500, color: '#DC2626' }}>{formatRM(viewSlip.processingFee)}</td>
                            </tr>
                            <tr style={{ borderTop: '1px solid #E2E8F0' }}>
                              <td colSpan="7" style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: '#374151' }}>Final Comm</td>
                              <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: '#1A8CFF' }}>{formatRM(viewSlip.totalPayout)}</td>
                            </tr>
                          </>
                        ) : (
                          <tr style={{ borderTop: '2px solid #E2E8F0' }}>
                            <td colSpan="7" style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: '#374151' }}>Total Payout</td>
                            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: '#1A8CFF' }}>{formatRM(viewSlip.totalPayout)}</td>
                          </tr>
                        )}
                      </tfoot>
                    </table>
                  </Box>
                );
              } catch {
                return <span style={{ fontSize: '0.8rem', color: '#94A3B8' }}>Unable to parse slip data</span>;
              }
            })()}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => handleDownloadPdf(viewSlip)} variant="contained" sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderRadius: '8px', background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', boxShadow: 'none' }}>Download PDF</Button>
            <Button onClick={() => setViewSlip(null)} sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none' }}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Rich preview for non-Commission slips (Incentive / Other) — the
          same SlipPdfFrame the accountant sees, so the recipient views the
          exact document, not just the listing fields. */}
      {viewOtherSlip && (
        <Dialog open={!!viewOtherSlip} onClose={() => setViewOtherSlip(null)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
          <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
            <span>{viewOtherSlip.type} Slip Preview</span>
            <IconButton onClick={() => setViewOtherSlip(null)} size="small"><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ background: '#F8FAFC', p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ boxShadow: '0 4px 24px rgba(15,23,42,0.08)' }}>
                <SlipPdfFrame
                  title={`${viewOtherSlip.type} Slip`}
                  agentName={viewOtherSlip.agentName}
                  period={viewOtherSlip.period}
                  dateIssued={viewOtherSlip.dateIssued}
                  totalPayout={viewOtherSlip.totalPayout ?? viewOtherSlip.amount}
                  status={viewOtherSlip.status}
                  sectionTitle={viewOtherSlip.type === 'Incentive' ? 'Incentive Achievement' : 'Payment Details'}
                >
                  <PdfBody slip={viewOtherSlip} />
                </SlipPdfFrame>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={() => handleDownloadRichPdf(viewOtherSlip)} startIcon={<DownloadIcon />} variant="contained" sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderRadius: '8px', background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', boxShadow: 'none' }}>Download PDF</Button>
            <Button onClick={() => setViewOtherSlip(null)} sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none' }}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Off-screen render target for Incentive / Other PDF export */}
      {richRenderSlip && (
        <div style={{ position: 'fixed', left: '-9999px', top: 0 }}>
          <SlipPdfFrame
            ref={richRenderRef}
            title={`${richRenderSlip.type} Slip`}
            agentName={richRenderSlip.agentName}
            period={richRenderSlip.period}
            dateIssued={richRenderSlip.dateIssued}
            totalPayout={richRenderSlip.totalPayout ?? richRenderSlip.amount}
            status={richRenderSlip.status}
            sectionTitle={richRenderSlip.type === 'Incentive' ? 'Incentive Achievement' : 'Payment Details'}
          >
            <PdfBody slip={richRenderSlip} />
          </SlipPdfFrame>
        </div>
      )}

      {/* Hidden PDF render template */}
      {renderSlip && (
        <div style={{ position: 'fixed', left: '-9999px', top: 0 }}>
          <div ref={slipRenderRef} style={{ width: '794px', fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#1a1a2e', background: '#fff' }}>
            <div style={{ background: '#fff', padding: '24px 32px 20px', display: 'flex', alignItems: 'center', gap: '16px', borderBottom: '3px solid #0a7cf8' }}>
              <img src="/nexwaveLogo.png" alt="nexwave" style={{ width: '48px', height: '48px', objectFit: 'contain' }} />
              <div style={{ width: '1px', height: '44px', background: '#e2e8f0' }}></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '26px', fontWeight: 800, color: '#0a7cf8', letterSpacing: '-0.5px', lineHeight: 1 }}>nexwave</div>
                <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px', lineHeight: 1.4 }}>
                  by <strong style={{ fontWeight: 600, color: '#64748b' }}>Webbey Sdn Bhd</strong> &nbsp;&middot;&nbsp; 199301017370 (272108-A)<br />
                  71-2, Jln Macalister, 10400 George Town, Pulau Pinang
                </div>
              </div>
              <div style={{ background: '#0a7cf8', borderRadius: '7px', padding: '9px 18px', textAlign: 'center' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>Commission Slip</div>
              </div>
            </div>
            {renderSlip.status && renderSlip.status !== 'Pending' && (
              <div style={{
                position: 'absolute', top: '24px', right: '200px', padding: '4px 14px', borderRadius: '5px',
                fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
                border: '2px solid',
                borderColor: renderSlip.status === 'Approved' ? '#065F46' : '#991B1B',
                color: renderSlip.status === 'Approved' ? '#065F46' : '#991B1B',
                backgroundColor: renderSlip.status === 'Approved' ? 'rgba(209,250,229,0.9)' : 'rgba(254,226,226,0.9)',
              }}>
                {renderSlip.status}
              </div>
            )}
            <div style={{ background: '#f8fafc', borderBottom: '1px solid #e8eef5', padding: '14px 32px', display: 'flex', gap: '36px', alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '8.5px', textTransform: 'uppercase', letterSpacing: '0.9px', color: '#94a3b8', fontWeight: 600 }}>Agent Name</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>{renderSlip.agentName}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '8.5px', textTransform: 'uppercase', letterSpacing: '0.9px', color: '#94a3b8', fontWeight: 600 }}>Period</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>{renderSlip.period}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '8.5px', textTransform: 'uppercase', letterSpacing: '0.9px', color: '#94a3b8', fontWeight: 600 }}>Date Issued</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>{formatDate(renderSlip.dateIssued)}</span>
              </div>
              <div style={{ marginLeft: 'auto', background: '#0a7cf8', borderRadius: '8px', padding: '10px 20px', textAlign: 'right' }}>
                <span style={{ fontSize: '8.5px', textTransform: 'uppercase', letterSpacing: '0.9px', color: 'rgba(255,255,255,0.7)', fontWeight: 600, display: 'block' }}>Total Commission Payout</span>
                <span style={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>{formatRM(renderSlip.totalPayout)}</span>
              </div>
            </div>
            <div style={{ padding: '16px 32px 10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h2 style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px', color: '#0a7cf8', whiteSpace: 'nowrap', margin: 0 }}>Commission Breakdown</h2>
              <div style={{ flex: 1, height: '1px', background: '#e8eef5' }}></div>
            </div>
            <div style={{ padding: '0 32px 24px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ background: '#0a7cf8', color: '#fff' }}>
                    <th style={{ padding: '9px 10px', textAlign: 'left', fontWeight: 600, fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.6px', borderRadius: '6px 0 0 6px' }}>Sale #</th>
                    <th style={{ padding: '9px 10px', textAlign: 'left', fontWeight: 600, fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Customer Name</th>
                    <th style={{ padding: '9px 10px', textAlign: 'left', fontWeight: 600, fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Type</th>
                    <th style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 600, fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Total Comm</th>
                    <th style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 600, fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>1st Pay</th>
                    <th style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 600, fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>2nd Pay</th>
                    <th style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 600, fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.6px' }}>3rd Pay</th>
                    <th style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 600, fontSize: '9.5px', textTransform: 'uppercase', letterSpacing: '0.6px', borderRadius: '0 6px 6px 0' }}>Payout</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => { try { return JSON.parse(renderSlip.slipData).map((item, i) => {
                    const repayTotal = (item.repayment || 0) + (item.repayment2 || 0) + (item.repayment3 || 0) + (item.repayment4 || 0) + (item.repayment5 || 0);
                    const latestRepayDate = [item.repayment5On, item.repayment4On, item.repayment3On, item.repayment2On, item.repaymentOn].find(d => d);
                    return (
                      <tr key={i} style={{ background: i % 2 === 1 ? '#f8fafc' : '#fff' }}>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #edf2f7', color: '#0a7cf8', fontWeight: 500 }}>{item.salesId || '\u2014'}</td>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #edf2f7', color: '#334155' }}>{item.customerName || '\u2014'}</td>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #edf2f7' }}>
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '9.5px', fontWeight: 600, background: item.type?.includes('TOC') ? '#fef3c7' : item.type?.includes('DOC') ? '#fce7f3' : '#dbeafe', color: item.type?.includes('TOC') ? '#b45309' : item.type?.includes('DOC') ? '#be185d' : '#1d4ed8' }}>{item.type || '\u2014'}</span>
                        </td>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #edf2f7', textAlign: 'right', color: '#334155' }}>{formatRM(item.totalComm)}</td>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #edf2f7', textAlign: 'right', color: '#334155' }}>
                          {item.firstPayment != null ? formatRM(item.firstPayment) : '\u2014'}
                          {item.firstPaymentOn && <div style={{ fontSize: '8px', color: '#94a3b8' }}>{formatDate(item.firstPaymentOn)}</div>}
                        </td>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #edf2f7', textAlign: 'right', color: '#334155' }}>
                          {item.secondPayment != null ? formatRM(item.secondPayment) : '\u2014'}
                          {item.secondPaymentOn && <div style={{ fontSize: '8px', color: '#94a3b8' }}>{formatDate(item.secondPaymentOn)}</div>}
                        </td>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #edf2f7', textAlign: 'right', color: repayTotal < 0 ? '#dc2626' : '#334155' }}>
                          {repayTotal !== 0 ? formatRM(repayTotal) : '\u2014'}
                          {latestRepayDate && <div style={{ fontSize: '8px', color: '#94a3b8' }}>{formatDate(latestRepayDate)}</div>}
                        </td>
                        <td style={{ padding: '9px 10px', borderBottom: '1px solid #edf2f7', textAlign: 'right', fontWeight: 600, color: '#1e293b' }}>{formatRM(item.payout || 0)}</td>
                      </tr>
                    );
                  }); } catch { return null; } })()}
                </tbody>
                <tfoot>
                  {renderSlip.processingFee > 0 ? (
                    <>
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'right', padding: '7px 14px 7px 10px', fontWeight: 600, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', background: '#f8fafc', borderTop: '2px solid #0a7cf8' }}>Gross Comm</td>
                        <td style={{ textAlign: 'right', padding: '7px 10px', fontWeight: 600, fontSize: '12px', color: '#1e293b', background: '#f8fafc', borderTop: '2px solid #0a7cf8' }}>{formatRM(renderSlip.grossCommission)}</td>
                      </tr>
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'right', padding: '7px 14px 7px 10px', fontWeight: 500, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', background: '#f8fafc' }}>Processing Fee</td>
                        <td style={{ textAlign: 'right', padding: '7px 10px', fontWeight: 500, fontSize: '12px', color: '#dc2626', background: '#f8fafc' }}>{formatRM(renderSlip.processingFee)}</td>
                      </tr>
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'right', padding: '9px 14px 9px 10px', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', background: '#eff6ff', borderTop: '1px solid #0a7cf8' }}>Final Comm</td>
                        <td style={{ textAlign: 'right', padding: '9px 10px', fontWeight: 700, fontSize: '13px', color: '#0a7cf8', background: '#eff6ff', borderTop: '1px solid #0a7cf8' }}>{formatRM(renderSlip.totalPayout)}</td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'right', padding: '9px 14px 9px 10px', fontWeight: 700, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', background: '#eff6ff', borderTop: '2px solid #0a7cf8' }}>Total Commission Payout</td>
                      <td style={{ textAlign: 'right', padding: '9px 10px', fontWeight: 700, fontSize: '13px', color: '#0a7cf8', background: '#eff6ff', borderTop: '2px solid #0a7cf8' }}>{formatRM(renderSlip.totalPayout)}</td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
            <div style={{ padding: '16px 32px 10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h2 style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px', color: '#0a7cf8', whiteSpace: 'nowrap', margin: 0 }}>Authorisation</h2>
              <div style={{ flex: 1, height: '1px', background: '#e8eef5' }}></div>
            </div>
            <div style={{ padding: '18px 32px 28px', display: 'flex', gap: '40px' }}>
              <div style={{ flex: 1, borderTop: '2px solid #0a7cf8', paddingTop: '10px' }}>
                <div style={{ fontWeight: 700, fontSize: '12px', color: '#1e293b' }}>Woo Jia Chee</div>
                <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px' }}>Prepared by</div>
                <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 500, marginTop: '1px' }}>Accounting</div>
                <div style={{ fontSize: '9.5px', color: '#94a3b8', marginTop: '7px' }}>Date: {formatDate(renderSlip.dateIssued)}</div>
              </div>
              <div style={{ flex: 1, borderTop: '2px solid #0a7cf8', paddingTop: '10px' }}>
                <div style={{ fontWeight: 700, fontSize: '12px', color: '#1e293b' }}>Tan Jin Sheng</div>
                <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px' }}>Approved by</div>
                <div style={{ fontSize: '10px', color: '#64748b', fontWeight: 500, marginTop: '1px' }}>Director</div>
                <div style={{ fontSize: '9.5px', color: '#94a3b8', marginTop: '7px' }}>Date: {formatDate(renderSlip.dateIssued)}</div>
              </div>
            </div>
            <div style={{ background: '#f8fafc', borderTop: '1px solid #e8eef5', padding: '10px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#0a7cf8' }}>nexwave</span>
              <span style={{ fontSize: '9px', color: '#94a3b8' }}>This is a computer-generated document &nbsp;&middot;&nbsp; Webbey Sdn Bhd 199301017370 (272108-A)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
